<!-- eslint-disable no-param-reassign -->
<!-- eslint-disable no-param-reassign -->
<template>
  <div class="received-samples-index">
    <Filters
      :filters="receivedSamplesFilters"
      filter-key="receivedSamples"
      :initial-data="filtersStatus.receivedSamples"
      :loading="filtersLoading"
    />

    <ReceivedSamplesTable
      ref="samplesTable"
      :table-columns="receivedSamplesColumns"
      :show-pagination="false"
      :enable-selection="canUseBulkApply"
      :massive-actions-options="massiveActionsOptions"
      :custom-row-class="getRowStatusClass"
      :height="tableHeight"
      :load-children="loadChildren"
      row-key="id"
      table-key="receivedSamples"
      @accept="acceptSamples"
      @refuse="refuseSamples"
      @downloadAttachment="downloadAttachment"
      @quarantine="quarantine"
      @massiveAction="bulkApplyState"
      @groupingInfo="groupingInfoSample"
    />

    <UpdateStatus
      v-if="openUpdateStatusModal && selectedSamples.length > 0"
      :samplesId="selectedSamplesId"
      :possibleActions="action"
      :message="message"
      :message-type="messageType"
      @dialogClose="handleUpdateStatusClose"
    />

    <SendToQuarantine
      v-if="openSendToQuarantine"
      :sample-id="quarantineSampleId"
      @dialogClose="handleDialogClose"
    />
  </div>
</template>

<script>
  // ReceivedSamplesIndex.vue - Sezione <script> completa

  import { mapGetters, mapActions } from 'vuex';
  import fileSaver from 'file-saver';

  import Filters from '@/components/Filters.vue';
  import { receivedSamplesFilters, receivedSamplesColumns } from '@/config';
  import { samplesApi, workPlanningApi } from '@/api';
  import {
    LAB_SAMPLE_STATUSES,
    SAMPLE_ACTIONS,
    SAMPLE_ACTIONS_DESC,
  } from '@/config/constants';
  import acl from '@/services/acl';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import axios from 'axios';

  import UpdateStatus from '../../components/UpdateStatus.vue';
  import ReceivedSamplesTable from './ReceivedSamplesTable.vue';
  import SendToQuarantine from './SendToQuarantine.vue';

  const possibleActions = {
    accept: {
      ACCEPT: SAMPLE_ACTIONS.ACCEPT,
    },
    reject: {
      REJECT: SAMPLE_ACTIONS.REJECT,
    },
  };

  export default {
    name: 'ReceivedSamplesIndex',
    mixins: [filterLoadingMixin, abortControllerMixin],
    components: {
      Filters,
      ReceivedSamplesTable,
      SendToQuarantine,
      UpdateStatus,
    },
    data() {
      return {
        receivedSamplesColumns,
        receivedSamplesFilters,
        status: [{ key: LAB_SAMPLE_STATUSES.PENDING }],
        massiveActionsOptions: [
          {
            label: this.$t(
              `enums.sampleActions.${
                SAMPLE_ACTIONS_DESC[SAMPLE_ACTIONS.ACCEPT]
              }`
            ),
            value: SAMPLE_ACTIONS.ACCEPT,
          },
          {
            label: this.$t(
              `enums.sampleActions.${
                SAMPLE_ACTIONS_DESC[SAMPLE_ACTIONS.REJECT]
              }`
            ),
            value: SAMPLE_ACTIONS.REJECT,
          },
          {
            label: this.$t('tableButtons.quarantine'),
            value: 'quarantine',
          },
        ],
        action: undefined,
        openUpdateStatusModal: false,
        openSendToQuarantine: false,
        quarantineSampleId: [],
        selectedSamples: [],
        message: '',
        messageType: undefined,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus', 'tabs', 'tablesStatus']),
      canUseBulkApply() {
        return acl.userCanUse(['receivedSamplesManage']);
      },
      selectedSamplesId() {
        return this.selectedSamples.map((sample) => sample.id);
      },
      tableHeight() {
        return `${window.innerHeight - 450}px`;
      },
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'parseQueryStringStatus',
        'resetTable',
      ]),
      async tableRows(query = null) {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          if (query && query.filterData) {
            const filterData = JSON.parse(query.filterData);
            this.status = filterData.status || this.status;
          }
          if (this.status.length === 0) {
            // restore default (pending)
            this.status = [
              { key: LAB_SAMPLE_STATUSES.PENDING },
              { key: LAB_SAMPLE_STATUSES.REJECTED },
            ];
          }

          const filterStatus = this.filtersStatus?.receivedSamples || {};
          const tableData = await samplesApi.received(
            {},
            {
              ...filterStatus,
              status: this.status,
            },
            { quarantine: false, groupsOnly: true },
            { signal }
          );

          const rows = tableData.data;
          await this.reloadExpandedChildren(rows, filterStatus, signal);

          this.updateTableRows({
            tableKey: 'receivedSamples',
            rows,
          });

          // el-table caches lazy-loaded children by row-key and keeps serving
          // them after :data is replaced, so already-expanded groups must be
          // re-synced explicitly or they keep showing pre-action rows
          await this.$nextTick();
          if (this.$refs.samplesTable) {
            this.$refs.samplesTable.syncLazyChildren(rows);
          }
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message || 'Error loading data',
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async reloadExpandedChildren(rows, filterStatus, signal) {
        const previousRows = this.tablesStatus?.receivedSamples?.rows || [];
        const loadedGroups = new Map(
          previousRows
            .filter((row) => row.childrenLoaded)
            .map((row) => [row.groupingId, row.children || []])
        );
        if (loadedGroups.size === 0) return;

        const groupsToRefresh = rows.filter((row) =>
          loadedGroups.has(row.groupingId)
        );
        if (groupsToRefresh.length === 0) return;

        let childrenByGroup = null;
        try {
          const childrenData = await samplesApi.receivedByGroups(
            groupsToRefresh.map((row) => row.groupingId),
            {},
            {
              ...filterStatus,
              status: this.status,
            },
            { signal }
          );
          childrenByGroup = childrenData.data.reduce((acc, child) => {
            (acc[child.groupingId] = acc[child.groupingId] || []).push(child);
            return acc;
          }, {});
        } catch (error) {
          if (axios.isCancel(error)) throw error;
          // keep the children already on screen rather than failing the whole
          // reload
        }

        groupsToRefresh.forEach((row) => {
          // eslint-disable-next-line no-param-reassign
          row.children = childrenByGroup
            ? childrenByGroup[row.groupingId] || []
            : loadedGroups.get(row.groupingId);
          // eslint-disable-next-line no-param-reassign
          row.childrenLoaded = true;
        });
      },
      async loadChildren(row, treeNode, resolve) {
        // Se i children sono già stati caricati, usa quelli
        if (row.childrenLoaded) {
          resolve(row.children);
          return;
        }

        try {
          const filterStatus = this.filtersStatus?.receivedSamples || {};

          // Carica i children per questo specifico gruppo
          const childrenData = await samplesApi.receivedByGroup(
            row.groupingId,
            {},
            {
              ...filterStatus,
              status: this.status,
            }
          );

          // Aggiorna la riga con i children caricati
          // eslint-disable-next-line no-param-reassign
          row.children = childrenData.data;
          // eslint-disable-next-line no-param-reassign
          row.childrenLoaded = true;

          // Aggiorna lo store se è già inizializzato
          if (this.tablesStatus?.receivedSamples?.rows) {
            const updatedRows = this.tablesStatus.receivedSamples.rows.map(
              (r) =>
                r.groupingId === row.groupingId
                  ? { ...r, children: childrenData.data, childrenLoaded: true }
                  : r
            );

            this.updateTableRows({
              tableKey: 'receivedSamples',
              rows: updatedRows,
            });
          }

          resolve(childrenData.data);
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message || 'Error loading children',
          });
          resolve([]);
        }
      },
      async ensureChildrenLoaded(row) {
        if (row.hasChildren && !row.childrenLoaded) {
          const loading = this.$loading({
            lock: true,
            background: 'rgba(48,53,82,0.9)',
          });

          try {
            const children = await new Promise((resolve) => {
              this.loadChildren(row, null, resolve);
            });

            if (this.tablesStatus?.receivedSamples?.rows) {
              const updatedRows = this.tablesStatus.receivedSamples.rows.map(
                (r) => {
                  if (r.groupingId === row.groupingId) {
                    return {
                      ...r,
                      children,
                      childrenLoaded: true,
                    };
                  }
                  return r;
                }
              );

              this.updateTableRows({
                tableKey: 'receivedSamples',
                rows: updatedRows,
              });
            }

            // eslint-disable-next-line no-param-reassign
            row.children = children;
            // eslint-disable-next-line no-param-reassign
            row.childrenLoaded = true;

            return children;
          } finally {
            loading.close();
          }
        }

        return row.children || [];
      },
      async acceptSamples(row) {
        // Carica i children se necessario
        await this.ensureChildrenLoaded(row);

        const samples =
          row.children && row.children.length > 0 ? row.children : [row];
        this.bulkApplyState(SAMPLE_ACTIONS.ACCEPT, samples);
      },
      async refuseSamples(row) {
        // Carica i children se necessario
        await this.ensureChildrenLoaded(row);

        const samples =
          row.children && row.children.length > 0 ? row.children : [row];
        this.bulkApplyState(SAMPLE_ACTIONS.REJECT, samples);
      },
      async groupingInfoSample(row) {
        const tabIsOpen = this.tabs.find((tab) => tab.name === 'groupingInfo');
        if (tabIsOpen) this.resetTable('groupings');
        const params = { id: [{ key: row.groupingId, label: row.groupingId }] };
        this.$router.push({
          name: 'groupingInfo',
          query: { filterData: JSON.stringify(params), filterKey: 'groupings' },
        });
      },
      handleUpdateStatusClose(status) {
        this.openUpdateStatusModal = false;
        this.selectedSamples = [];
        this.action = undefined;
        if (status.success) this.tableRows();
      },
      async bulkApplyState(state, selectedRows) {
        this.selectedSamples = selectedRows;
        this.message = '';
        this.messageType = undefined;
        switch (state) {
          case SAMPLE_ACTIONS.ACCEPT: {
            if (
              this.selectedSamples.some(
                (row) => row.latestPrint < row.groupingUpdatedAt
              )
            ) {
              this.message = this.$tc(
                'receivedSamples.accept.needsReprint',
                this.selectedSamples.length
              );
              this.messageType = 'warning';
              await this.$confirm(this.message, this.$t('global.warning'), {
                confirmButtonText: this.$t('global.confirm'),
                cancelButtonText: this.$t('global.cancel'),
              });
            }
            const hasMultipleLocations = this.selectedSamples.some(
              (row) => row.hasMultipleLocations
            );
            const confirmMessage = hasMultipleLocations
              ? `${this.$t('receivedSamples.accept.warning')}\n${this.$t(
                  'global.confirmAction'
                )}`
              : this.$t('global.confirmAction');
            this.$confirm(confirmMessage, this.$t('global.warning'), {
              confirmButtonText: this.$t('global.confirm'),
              cancelButtonText: this.$t('global.cancel'),
            }).then(() => {
              this.action = possibleActions.accept;
              this.openUpdateStatusModal = true;
            });
            break;
          }
          case SAMPLE_ACTIONS.REJECT:
            this.action = possibleActions.reject;
            this.openUpdateStatusModal = true;
            break;
          case 'quarantine':
            this.selectedSamples = [];
            this.quarantineSampleId = selectedRows.map((r) => r.id);
            this.openSendToQuarantine = true;
            break;
          default:
            break;
        }
      },
      async downloadAttachment(row) {
        try {
          if (row.groupHasAttachments) {
            const where = {};
            where.groupingId = row.groupingId;
            const blob = await workPlanningApi.attachmentsZip({ where });
            fileSaver.saveAs(
              blob.data,
              `group-${row.groupingId}-attachment.zip`
            );
            return false;
          }

          // Se è un singolo sample, scarica i suoi attachments
          const blob = await samplesApi.downloadAttachmentsZip(row.id);
          fileSaver.saveAs(blob.data, `sample-${row.id}-attachment.zip`);
          return false;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message ||
              'Error downloading attachment',
          });
          return false;
        }
      },
      handleDialogClose(status) {
        this.openSendToQuarantine = false;
        this.quarantineSampleId = [];
        if (status.success) this.tableRows();
      },
      async quarantine(row) {
        await this.ensureChildrenLoaded(row);
        this.quarantineSampleId =
          row.children && row.children.length > 0
            ? row.children.map((c) => c.id)
            : [row.id];
        this.openSendToQuarantine = true;
      },
      getRowStatusClass({ row }) {
        if (row.status && row.status === LAB_SAMPLE_STATUSES.REJECTED) {
          return 'sample-row-rejected';
        }
        if (!row.children && row.stabilityTimepointToken) {
          return 'sample-row-stability';
        }
        return '';
      },
    },
    created() {
      const query = { ...this.$route.query };
      if (!query.filterData) {
        query.filterData = JSON.stringify({
          status: [
            {
              key: LAB_SAMPLE_STATUSES.PENDING,
              label: this.$t('filters.toBeAccepted'),
            },
          ],
        });
        query.filterKey = 'receivedSamples';
      }
      this.parseQueryStringStatus(query);
      this.tableRows(query);
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows(to.query);
      next();
    },
  };
</script>

<style lang="scss">
  .received-samples-index {
    .el-table {
      &__row {
        &.sample-row-rejected {
          background: map-get(
            map-get($sample-statuses, 'rejected'),
            'background'
          );
        }
        &.sample-row-stability {
          background: #1b3a4b;
        }
      }
    }
  }
</style>
